// kasir.view — Tutup Kasir Harian: cocokkan uang yang MASUK dengan unit yang KELUAR, per tanggal & cabang.
//
// Angka di layar ini memakai KAS AKTUAL (uang yang benar-benar diterima kasir), BUKAN omzet yang
// diakui setelah verifikasi Finance. Tujuannya beda: menghitung isi laci sore hari. Aturan omzet
// (lihat domain/money) tidak tersentuh — perbedaan ini dinyatakan di layar agar tak tertukar.
import { rp, esc, todayISO, rpFmt, rpVal } from '@domain/format';
import { currentUser } from '@core/session';
import { kasHarian, selisih, totalSetoran, analisaSelisih, kasPelunasanTempo, METODE,
  baris1Penutupan, ringkasPenutupan, type KasRow, type Petunjuk, type BarisPenutupan } from '@domain/kasir';
import { akunUntukCara, analisaSelisih as analisaAkun, type DugaanSelisih } from '@domain/akunkas';
import * as akunRepo from './akunkas.repo';
import { isPiutangCara } from '@domain/money';
import { openModal } from '@ui/modal';
import { toast } from '@ui/toast';
import { downloadXLS } from '@ui/xls';
import { listCabang } from '@modules/cabang/cabang.repo';
import * as repo from './kasir.repo';
import { paidForDate, type PaidKas as PayoutRow } from '@modules/finance/payout.repo';
import * as svc from './kasir.service';

let kTgl = '', kCab = '';
/** Layar mana yang sedang dibuka: penutupan satu hari, atau riwayatnya. */
let kMode: 'hari' | 'riwayat' = 'hari';
let rBulan = '', rTahun = '';

const isMgr = (): boolean => ['Owner', 'Manager'].includes(currentUser()?.role ?? '');
/** Yang boleh MENUTUP kasir (isi hitungan fisik). Teknisi & Supplier tidak. */
const bolehTutup = svc.bolehTutup;
/** Yang boleh MEMVERIFIKASI penutupan — pemisahan tugas, sama seperti lunas→verifikasi. */
const bolehVerif = svc.bolehVerif;

const stat = (l: string, v: string, c = 'var(--navy)'): string =>
  `<div class="stat"><div class="lbl">${esc(l)}</div><div class="num" style="color:${c}">${v}</div></div>`;

const sumberLabel: Record<string, string> = { serah: 'Serah terima', dp: 'DP terima servis', tempo: 'Pelunasan tempo', refund: 'Refund', komisi: 'Komisi (dibayar)' };

export async function renderTutupKasir(root: HTMLElement): Promise<void> {
  const u = currentUser()!;
  if (!kTgl) kTgl = todayISO();
  if (!kCab) kCab = isMgr() ? '' : (u.outlet ?? '');
  if (kMode === 'riwayat') { await renderRiwayat(root); return; }
  root.innerHTML = `<div class="panel"><div class="empty">Memuat…</div></div>`;

  let tickets: Awaited<ReturnType<typeof repo.ticketsForDate>>;
  let tanpaTgl: Awaited<ReturnType<typeof repo.tempoTanpaTanggal>>;
  let close: repo.DailyClose | null;
  let payoutsTunai: PayoutRow[] = [];
  let cabangs: { kode: string; nama: string }[] = [];
  try {
    [tickets, tanpaTgl, close] = await Promise.all([
      repo.ticketsForDate(kTgl, kCab || null),
      repo.tempoTanpaTanggal(kCab || null),
      repo.getClose(kTgl, kCab || null),
    ]);
    // Komisi TUNAI yang DIBAYAR hari ini = uang keluar laci (langkah bayar, bukan approve) — Transfer tak lewat laci.
    try { payoutsTunai = (await paidForDate(kTgl, kCab || null)).filter((p) => p.paid_via === 'Tunai'); }
    catch { /* payout opsional — jangan gagalkan tutup kasir */ }
    if (isMgr()) cabangs = await listCabang();
  } catch (e) { root.innerHTML = `<div class="panel err">${esc((e as Error).message)}</div>`; return; }

  // Akun kas & uang keluar non-tiket (beban, setoran). Opsional: kalau gagal dimuat, layar tetap
  // bekerja seperti sebelumnya — jangan sampai kasir tak bisa tutup buku gara-gara satu query.
  let akunKas: akunRepo.AkunRow[] = [];
  let mutasi: akunRepo.MutasiRow[] = [];
  try {
    akunKas = (await akunRepo.listAkun()).filter((a) => !a.outlet || !kCab || a.outlet === kCab);
    mutasi = await akunRepo.mutasiTanggal(kTgl, kCab || null);
  } catch { /* akun kas belum disiapkan */ }

  const komisiKeluar: KasRow[] = payoutsTunai.map((p) => ({
    no: p.no, sumber: 'komisi', metode: 'Tunai', jumlah: p.amount,
    custNama: p.emp_nama, unit: `Komisi ${p.periode}`, campuran: false,
  }));
  const kas = kasHarian(tickets, kTgl, kCab || null, komisiKeluar);
  const terkunci = !!close?.verified_at && !isMgr(); // sudah diverifikasi → dikunci (kecuali O/M)
  // Satu laci FISIK milik satu cabang: menutup "Semua cabang" tak bermakna (uang tak bisa dihitung
  // untuk dua laci sekaligus). Tampilan gabungan tetap boleh dibaca sebagai ringkasan.
  const bolehTutupTgl = !!kCab;

  // Unit KELUAR hari itu — inti pertanyaan Apo: "uang masuk cocok tidak dengan HP yang keluar".
  const keluar = tickets.filter((t) => (t.tglAmbilISO ?? '').slice(0, 10) === kTgl);

  // Yang perlu diperiksa: HP keluar tapi uangnya tak jelas.
  // Piutang/Tempo = keputusan kredit RESMI, bukan anomali. Kalau ikut, nominalnya jadi tersangka
  // palsu di analisa selisih — kasir dikirim mengejar hantu.
  const anomali = keluar.filter((t) => !t.lunas && !isPiutangCara(t.caraBayar));
  // Dihitung dari daftar LENGKAP (bukan dari himpunan tiket harian) supaya angkanya stabil.
  const totalTanpaTgl = tanpaTgl.reduce((a, t) => a + kasPelunasanTempo(t), 0);

  const expectLive: Record<string, number> = {};
  for (const m of METODE) if (kas.perMetode[m]) expectLive[m] = kas.perMetode[m] ?? 0;
  // Hari yang SUDAH ditutup memakai angka BEKU saat penutupan. Tanpa ini, mengedit tagihan lama
  // akan memunculkan "selisih" baru di hari yang sudah beres & diverifikasi — dan layar bercerita
  // beda dari yang tersimpan di database.
  const beku = !!close?.closed_at && !!close.expect && Object.keys(close.expect).length > 0;
  const expect = beku ? close!.expect : expectLive;
  const actual = close?.actual ?? {};
  const dif = selisih(expect, actual);
  const setoran: repo.SetoranRow[] = close?.setoran ?? [];

  const rowKas = (r: KasRow): string =>
    `<tr><td><b>${esc(r.no)}</b>${r.campuran ? ' <span class="pill" style="background:var(--warnbg);color:var(--warn);font-size:10px" title="Tiket ini ber-DP: DP dan pelunasan bisa beda metode, sedangkan tiket hanya menyimpan satu. Periksa manual.">metode campuran</span>' : ''}</td>
      <td>${esc(r.custNama)}</td><td>${esc(r.unit)}</td><td>${esc(sumberLabel[r.sumber] ?? r.sumber)}</td>
      <td>${esc(r.metode)}</td><td class="r"><b>${rp(r.jumlah)}</b></td></tr>`;

  const inputAktual = (m: string): string =>
    `<td class="r"><input class="ks_act" data-m="${esc(m)}" inputmode="numeric" value="${actual[m] != null ? rpFmt(String(actual[m])) : ''}" placeholder="${rp(expect[m] ?? 0)}" ${terkunci || !bolehTutup() || !bolehTutupTgl ? 'disabled' : ''} style="max-width:130px;text-align:right"></td>`;

  // ── Baris per AKUN (permintaan Apo 22 Jul): tunai, tiap rekening bank, QRIS — SELALU tampil
  // walau nihil, supaya kasir menghitungnya secara sadar. Kunci baris = nama akun; nota lama
  // yang cuma menulis "Transfer" jatuh ke rekening pertama lewat `akunUntukCara`.
  const perAkun = new Map<string, number>();
  const tambah = (nama: string, v: number): void => { perAkun.set(nama, (perAkun.get(nama) ?? 0) + v); };
  // Saldo awal laci = KAS KECIL (model imprest, keputusan Apo 22 Jul): angkanya ditetapkan di
  // Pengaturan → Akun Kas, jadi kasir tak perlu mengarang saldo awal tiap pagi. Rekening & QRIS
  // tak punya "laci", saldo awalnya nol pada perhitungan harian ini.
  for (const a of akunKas) perAkun.set(a.nama, a.jenis === 'tunai' ? a.kasKecil : 0);
  const namaAkun = (r: KasRow): string =>
    akunUntukCara(r.caraAsli ?? r.metode, akunKas, kCab || null)?.nama ?? r.metode;
  for (const r of kas.masuk) tambah(namaAkun(r), r.jumlah);
  for (const r of kas.keluar) tambah(namaAkun(r), -r.jumlah);
  for (const m of mutasi) {
    const a = akunKas.find((x) => x.id === m.akunId);
    if (a) tambah(a.nama, m.arah === 'masuk' ? m.jumlah : -m.jumlah);
  }
  const pakaiAkun = akunKas.length > 0;
  const metodeAda: string[] = pakaiAkun
    ? [...new Set([...akunKas.map((a) => a.nama), ...[...perAkun.keys()].filter((k) => (perAkun.get(k) ?? 0) !== 0)])]
    : METODE.filter((m) => (expect[m] ?? 0) !== 0 || actual[m] != null);
  // Angka "seharusnya" hari berjalan diambil dari perhitungan per akun; hari yang sudah ditutup
  // tetap memakai angka bekunya (jangan sampai layar bercerita beda dari yang tersimpan).
  if (pakaiAkun && !beku) for (const k of metodeAda) expectLive[k] = perAkun.get(k) ?? 0;
  const barisMetode = metodeAda.map((m) => {
    const d = dif[m] ?? 0;
    const info = akunKas.find((a) => a.nama === m);
    return `<tr><td><b>${esc(m)}</b>${m === 'Belum dicatat' ? ' <span class="pill" style="background:var(--warnbg);color:var(--warn);font-size:10px">metode tak tercatat</span>' : ''}
      ${info ? `<div style="font-size:11px;color:var(--muted)">${esc(info.jenis === 'tunai'
        ? `dihitung fisik di laci · kas kecil ${rp(info.kasKecil)}`
        : info.jenis === 'qris' ? 'cocokkan ke laporan settlement'
        : `cocokkan ke mutasi ${info.bankNama ?? 'bank'}${info.noRek ? ' ' + info.noRek : ''}`)}</div>` : ''}</td>
      <td class="r">${rp(expect[m] ?? 0)}</td>${inputAktual(m)}
      <td class="r" style="color:${d === 0 ? 'var(--muted)' : (d > 0 ? 'var(--ok)' : 'var(--danger)')};font-weight:700">${actual[m] == null ? '—' : (d > 0 ? '+' : '') + rp(d)}</td></tr>`;
  }).join('') || `<tr><td colspan="4" class="empty">Belum ada akun kas. Siapkan di Pengaturan → 💵 Akun Kas supaya tunai &amp; tiap rekening bisa dicocokkan sendiri-sendiri.</td></tr>`;

  // Uang keluar non-tiket hari itu — tanpa ini laci "kurang" tanpa penjelasan.
  const keluarLain = mutasi.filter((m) => m.arah === 'keluar');
  const barisKeluar = keluarLain.map((m) => {
    const a = akunKas.find((x) => x.id === m.akunId);
    return `<tr><td>${esc(m.jenis === 'beban' ? '🧾 Beban' : m.jenis === 'setoran' ? '🏦 Setoran' : m.jenis === 'supplier' ? '🔩 Bayar supplier' : m.jenis)}</td>
      <td>${esc(m.catatan ?? '—')}${m.refId ? ` <span style="color:var(--muted);font-size:11px">${esc(m.refId)}</span>` : ''}</td>
      <td>${esc(a?.nama ?? '—')}</td><td class="r"><b>${rp(m.jumlah)}</b></td></tr>`;
  }).join('') || '<tr><td colspan="4" class="empty">Tak ada uang keluar tercatat hari ini (di luar refund & komisi).</td></tr>';
  const totalKeluarLain = keluarLain.reduce((a, m) => a + m.jumlah, 0);

  const totalDif = metodeAda.reduce((a, m) => a + (actual[m] == null ? 0 : (dif[m] ?? 0)), 0);
  // Berapa yang perlu disetor agar laci kembali ke kas kecil (khas model imprest).
  const kasKecilTotal = akunKas.filter((a) => a.jenis === 'tunai').reduce((a, x) => a + x.kasKecil, 0);
  const tunaiSeharusnya = akunKas.filter((a) => a.jenis === 'tunai')
    .reduce((a, x) => a + (expect[x.nama] ?? perAkun.get(x.nama) ?? 0), 0);
  const setoranTercatat = setoran.reduce((a, x) => a + (x.jumlah ?? 0), 0);
  const setorSaran = Math.max(0, Math.round(tunaiSeharusnya - kasKecilTotal - setoranTercatat));
  const adaAktual = metodeAda.some((m) => actual[m] != null);
  const belumBayarSel = anomali.map((t) => ({ no: t.no, jumlah: (t.biaya ?? 0) + (t.biayaSimpan ?? 0) - (t.dp ?? 0) }));

  const setoranRows = setoran.map((s, i) => `<tr><td>${s.jenis === 'bank' ? '🏦 Bank' : '💵 Tunai'}</td><td>${esc(s.tujuan ?? '—')}</td>
      <td>${esc(s.ref ?? '—')}</td><td class="r"><b>${rp(s.jumlah)}</b></td>
      <td>${terkunci || !bolehTutup() ? '' : `<button class="ico-btn" data-delset="${i}" title="Hapus setoran" aria-label="Hapus setoran ${esc(s.tujuan ?? '')}">🗑️</button>`}</td></tr>`).join('')
    || '<tr><td colspan="5" class="empty">Belum ada setoran dicatat.</td></tr>';

  const cabOpts = cabangs.map((c) => `<option value="${esc(c.kode)}" ${kCab === c.kode ? 'selected' : ''}>${esc(c.nama)}</option>`).join('');

  root.innerHTML = `
    <div class="scope-note">Cocokkan <b>uang yang masuk</b> dengan <b>unit yang keluar</b> pada satu hari. Angka di sini = <b>kas aktual yang diterima kasir</b> (bukan omzet yang diakui setelah verifikasi Finance — tujuannya beda: menghitung isi laci). Tiap cabang punya lacinya sendiri.</div>
    <div class="toolbar" style="display:flex;gap:8px;align-items:center;margin:10px 0;flex-wrap:wrap">
      <label style="font-size:13px;color:var(--muted)">Tanggal</label>
      <input type="date" id="ks_tgl" value="${esc(kTgl)}" aria-label="Pilih tanggal" style="height:30px;font-size:12.5px;padding:0 8px;border:1px solid var(--line);border-radius:8px">
      ${isMgr() ? `<select id="ks_cab" aria-label="Pilih cabang"><option value="">Semua cabang</option>${cabOpts}</select>` : ''}
      <button class="btn" id="ks_kemarin" style="padding:5px 10px">← Kemarin</button>
      <button class="btn" id="ks_hariini" style="padding:5px 10px">Hari ini</button>
      <button class="btn" id="ks_riwayat" style="margin-left:auto;padding:5px 10px">📜 Riwayat &amp; laporan bulanan</button>
      <button class="btn" id="ks_xls" style="padding:5px 10px">⬇️ Excel</button>
    </div>

    ${beku ? '<div class="scope-note" style="font-size:11.5px">🔒 Angka "seharusnya" di bawah adalah <b>angka beku saat penutupan</b> — perubahan data setelah itu tidak menggesernya.</div>' : ''}
    ${close?.verified_at
      ? `<div class="scope-note" style="background:var(--okbg);color:var(--ok)">✓ Penutupan hari ini sudah <b>diverifikasi</b> oleh ${esc(close.verified_by ?? '—')}${isMgr() ? '' : ' — terkunci'}.</div>`
      : close?.closed_at
        ? `<div class="scope-note" style="background:var(--warnbg);color:var(--warn)">🔒 Sudah <b>ditutup</b> oleh ${esc(close.closed_by ?? '—')} — menunggu verifikasi Finance/Owner.</div>`
        : ''}

    <div class="stat-grid" style="margin:8px 0 12px">
      ${stat('Unit keluar', String(keluar.length))}
      ${stat('Uang masuk', rp(kas.totalMasuk), 'var(--ok)')}
      ${stat('Uang keluar (refund + komisi tunai)', rp(kas.totalKeluar), 'var(--danger)')}
      ${stat('Piutang baru (tempo)', rp(kas.tempoBaru), 'var(--warn)')}
      ${kasKecilTotal > 0 ? stat('Kas kecil (saldo laci)', rp(kasKecilTotal), 'var(--muted)') : ''}
      ${adaAktual ? stat('Selisih', (totalDif > 0 ? '+' : '') + rp(totalDif), totalDif === 0 ? 'var(--ok)' : 'var(--danger)') : ''}
    </div>
    ${setorSaran > 0 ? `<div class="scope-note" style="background:var(--okbg);color:var(--ok)">
      💰 Agar laci kembali ke kas kecil ${rp(kasKecilTotal)}, yang perlu disetor hari ini <b>${rp(setorSaran)}</b>.
      <button class="btn" id="ks_setorcepat" style="border-color:var(--ok);color:var(--ok);margin-left:6px">➕ Catat setoran ${rp(setorSaran)}</button></div>` : ''}
    <div id="ks_selisih_live"></div>${'' /* diisi live saat hitungan fisik diketik / setelah tutup — lihat paintSelisih() */}

    <div class="ttl" style="font-size:14px;margin:14px 0 4px">💰 Uang masuk & keluar per metode</div>
    ${!kCab
      ? `<div class="scope-note" style="background:var(--warnbg);color:var(--warn)">Sedang melihat <b>Semua cabang</b> — laci tiap cabang berbeda dan tak bisa dicocokkan jadi satu angka. <b>Pilih satu cabang</b> di atas untuk mencocokkan laci &amp; tutup buku. (Angka di kartu ringkasan tetap bisa dibaca sebagai gambaran.)</div>`
      : `<div class="scope-note" style="font-size:11.5px">Tunai masuk laci · Transfer masuk rekening · QRIS cair H+1 — sengaja tidak digabung, karena satu angka gabungan tak bisa dicocokkan ke mana pun. <b>Tempo bukan uang masuk</b> (jadi piutang).</div>
    <table class="data nosort"><thead><tr><th>${pakaiAkun ? 'Akun kas' : 'Metode'}</th><th class="r">Seharusnya</th><th class="r">Dihitung fisik</th><th class="r">Selisih</th></tr></thead><tbody>${barisMetode}</tbody></table>`}

    <div class="ttl" style="font-size:14px;margin:16px 0 4px">🧾 Uang keluar hari ini <span style="font-size:11.5px;font-weight:400;color:var(--muted)">(beban &amp; setoran — sudah ikut dikurangi di tabel atas)</span></div>
    <table class="data nosort"><thead><tr><th>Jenis</th><th>Keterangan</th><th>Dari akun</th><th class="r">Jumlah</th></tr></thead>
      <tbody>${barisKeluar}</tbody>
      ${totalKeluarLain > 0 ? `<tfoot><tr><th colspan="3" class="r">Total uang keluar</th><th class="r">${rp(totalKeluarLain)}</th></tr></tfoot>` : ''}</table>

    ${totalTanpaTgl > 0 ? `<div class="scope-note" style="background:var(--warnbg);color:var(--warn);margin-top:8px">⚠ ${rp(totalTanpaTgl)} pelunasan tempo <b>tanpa jejak tanggal</b> (data lama, ${tanpaTgl.length} tiket) — tidak dimasukkan ke hari mana pun agar laporan tidak berbohong. Pelunasan baru sudah dicatat tanggalnya otomatis.</div>` : ''}

    <div class="ttl" style="font-size:14px;margin:16px 0 4px">🏦 Setoran <span style="font-size:11.5px;font-weight:400;color:var(--muted)">(bank & tunai dicatat terpisah)</span></div>
    <table class="data nosort"><thead><tr><th>Jenis</th><th>Tujuan</th><th>Ref/Bukti</th><th class="r">Jumlah</th><th></th></tr></thead><tbody>${setoranRows}</tbody></table>
    <div style="display:flex;gap:14px;margin-top:6px;font-size:12.5px;flex-wrap:wrap">
      <span>Setoran bank: <b>${rp(totalSetoran(setoran, 'bank'))}</b></span>
      <span>Setoran tunai: <b>${rp(totalSetoran(setoran, 'tunai'))}</b></span>
      ${terkunci || !bolehTutup() || !bolehTutupTgl ? '' : `<button class="btn" id="ks_addset" style="padding:4px 10px;margin-left:auto">➕ Catat setoran</button>`}
    </div>

    <div class="ttl" style="font-size:14px;margin:16px 0 4px">📱 Unit keluar hari ini <span class="pill" style="background:var(--ice);color:var(--navy)">${keluar.length}</span></div>
    <div style="overflow-x:auto"><table class="data"><thead><tr><th>No. Servis</th><th>Pelanggan</th><th>Unit</th><th class="r">Tagihan</th><th class="r">DP</th><th>Cara bayar</th><th>Status</th></tr></thead><tbody>${
      keluar.map((t) => `<tr><td><b>${esc(t.no)}</b></td><td>${esc(t.custNama ?? '—')}</td><td>${esc(t.unit ?? '—')}</td>
        <td class="r">${rp((t.biaya ?? 0) + (t.biayaSimpan ?? 0))}</td><td class="r">${(t.dp ?? 0) > 0 ? rp(t.dp ?? 0) : '—'}</td>
        <td>${esc(t.caraBayar ?? '—')}</td><td>${t.lunas ? '<span class="pill" style="background:var(--okbg);color:var(--ok)">✓ Lunas</span>' : (isPiutangCara(t.caraBayar) ? `<span class="pill" style="background:var(--warnbg);color:var(--warn)">${esc(t.caraBayar ?? 'Piutang')}</span>` : '<span class="pill" style="background:var(--dangerbg);color:var(--danger)">⚠ Belum bayar</span>')}</td></tr>`).join('')
      || '<tr><td colspan="7" class="empty">Tidak ada unit keluar pada tanggal ini.</td></tr>'}</tbody></table></div>

    ${anomali.length ? `<div class="scope-note" style="background:var(--dangerbg);color:var(--danger);margin-top:8px"><b>⚠ ${anomali.length} unit keluar tanpa pembayaran & bukan tempo resmi</b> — periksa: ${anomali.map((t) => esc(t.no)).join(', ')}</div>` : ''}

    <div class="ttl" style="font-size:14px;margin:16px 0 4px">📝 Rincian uang masuk</div>
    <div style="overflow-x:auto"><table class="data"><thead><tr><th>No. Servis</th><th>Pelanggan</th><th>Unit</th><th>Sumber</th><th>Metode</th><th class="r">Jumlah</th></tr></thead><tbody>${
      [...kas.masuk.map(rowKas), ...kas.keluar.map((r) => rowKas({ ...r, jumlah: -r.jumlah }))].join('')
      || '<tr><td colspan="6" class="empty">Belum ada pergerakan uang.</td></tr>'}</tbody></table></div>

    <div class="field" style="margin-top:14px"><label>Catatan penutupan${adaAktual && totalDif !== 0 ? ' <span class="req">* (wajib bila ada selisih)</span>' : ''}</label>
      <textarea id="ks_catatan" rows="2" ${terkunci || !bolehTutup() || !bolehTutupTgl ? 'disabled' : ''} placeholder="mis. selisih Rp 20.000 karena kembalian kurang">${esc(close?.catatan ?? '')}</textarea></div>

    <div style="display:flex;gap:8px;margin-top:10px;flex-wrap:wrap">
      ${bolehTutup() && !terkunci && bolehTutupTgl ? `<button class="btn primary" id="ks_tutup">${close?.closed_at ? '💾 Perbarui Penutupan' : '🔒 Tutup Hari'}</button>` : ''}
      ${bolehTutup() && !terkunci && !bolehTutupTgl ? `<span class="scope-note" style="margin:0">Pilih <b>satu cabang</b> dulu untuk menutup kasir — satu laci fisik milik satu cabang.</span>` : ''}
      ${bolehVerif() && close?.closed_at && !close?.verified_at ? `<button class="btn" id="ks_verif" style="border-color:var(--ok);color:var(--ok)">✓ Verifikasi Penutupan</button>` : ''}
    </div>`;

  // ---- wiring ----
  const refresh = (): void => { void renderTutupKasir(root); };
  const tgl = root.querySelector<HTMLInputElement>('#ks_tgl');
  if (tgl) tgl.onchange = (): void => { kTgl = tgl.value || todayISO(); refresh(); };
  const cab = root.querySelector<HTMLSelectElement>('#ks_cab');
  if (cab) cab.onchange = (): void => { kCab = cab.value; refresh(); };
  root.querySelector('#ks_hariini')?.addEventListener('click', () => { kTgl = todayISO(); refresh(); });
  root.querySelector('#ks_kemarin')?.addEventListener('click', () => {
    const d = new Date(kTgl + 'T12:00:00'); d.setDate(d.getDate() - 1); // tengah hari → aman dari zona waktu
    kTgl = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    refresh();
  });
  root.querySelectorAll<HTMLInputElement>('input.ks_act').forEach((el) =>
    el.addEventListener('input', () => { el.value = el.value.trim() ? rpFmt(el.value) : ''; }));

  const bacaAktual = (): Record<string, number> => {
    const out: Record<string, number> = {};
    root.querySelectorAll<HTMLInputElement>('input.ks_act').forEach((el) => {
      if (el.value.trim()) out[el.dataset.m ?? ''] = rpVal(el.value);
    });
    return out;
  };

  // Dialog "Cari letak selisih" — dihitung dari aktual (bisa LIVE saat mengetik, bukan hanya tersimpan).
  async function openRekon(act: Record<string, number>): Promise<void> {
    const d = selisih(expect, act);
    const tot = metodeAda.reduce((a, m) => a + (act[m] == null ? 0 : (d[m] ?? 0)), 0);
    let bebanNganggur = 0;
    try { bebanNganggur = await akunRepo.bebanBelumDibayar(kTgl, kCab || null); } catch { /* opsional */ }
    const belumDicatat = kas.masuk.filter((r) => r.metode === 'Belum dicatat').reduce((a, r) => a + r.jumlah, 0);
    const setoranHariIni = mutasi.filter((m) => m.jenis === 'setoran' && m.arah === 'keluar').reduce((a, m) => a + m.jumlah, 0);
    const qrisAkun = akunKas.find((a) => a.jenis === 'qris');
    const perAkunDugaan: { akun: string; dugaan: DugaanSelisih[] }[] = metodeAda
      .filter((m) => act[m] != null && (d[m] ?? 0) !== 0)
      .map((m) => ({ akun: m, dugaan: analisaAkun({
        selisih: d[m] ?? 0, jenisAkun: akunKas.find((a) => a.nama === m)?.jenis ?? 'tunai',
        belumDicatat, tempoBaru: kas.tempoBaru,
        qrisBelumCair: qrisAkun ? (perAkun.get(qrisAkun.nama) ?? 0) : 0,
        bebanBelumDibayar: bebanNganggur, setoranHariIni,
      }) }));
    rekonDialog(tot, analisaSelisih(tot, kas, setoran, belumBayarSel, totalTanpaTgl), d, perAkunDugaan);
  }

  // Panel selisih + tersangka RINGKAS, di-refresh LIVE tiap hitungan fisik diketik (Apo: "begitu ada
  // selisih langsung ada analisa"). Detail per-akun tetap lewat tombol "Cari letak selisih".
  const selBox = root.querySelector('#ks_selisih_live') as HTMLElement | null;
  const paintSelisih = (act: Record<string, number>): void => {
    if (!selBox) return;
    if (!Object.keys(act).length) { selBox.innerHTML = ''; return; }
    const d = selisih(expect, act);
    const tot = metodeAda.reduce((a, m) => a + (act[m] == null ? 0 : (d[m] ?? 0)), 0);
    if (tot === 0) { selBox.innerHTML = '<div class="scope-note" style="background:var(--okbg);color:var(--ok)">✓ Cocok — hitungan fisik sama dengan seharusnya.</div>'; return; }
    const dug = analisaSelisih(tot, kas, setoran, belumBayarSel, totalTanpaTgl);
    selBox.innerHTML = `<div class="scope-note" style="background:var(--dangerbg);color:var(--danger)">
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap"><b>Selisih ${(tot > 0 ? '+' : '') + rp(tot)}</b> — uang fisik ${tot > 0 ? 'LEBIH banyak' : 'KURANG'} dari catatan.
        <button class="btn" id="ks_rekon" style="border-color:var(--danger);color:var(--danger);margin-left:auto">🔍 Cari letak selisih (detail)</button></div>
      ${dug.length ? `<div style="margin-top:6px;font-size:12px;color:var(--txt)">Kemungkinan penyebab:<ul style="margin:3px 0 0;padding-left:18px">${dug.slice(0, 3).map((x) => `<li><b>${esc(x.judul)}</b> ${rp(x.jumlah)}${x.cocokPersis ? ' <span style="color:var(--ok)">— cocok persis ✓</span>' : ''}</li>`).join('')}</ul></div>`
        : '<div style="margin-top:6px;font-size:12px">Tak terdeteksi penyebab umum — periksa nota lewat tombol.</div>'}</div>`;
    selBox.querySelector('#ks_rekon')?.addEventListener('click', () => void openRekon(act));
  };
  root.querySelectorAll<HTMLInputElement>('input.ks_act').forEach((el) =>
    el.addEventListener('input', () => paintSelisih(bacaAktual())));
  paintSelisih(actual); // keadaan awal (mis. hari yang sudah ditutup dengan aktual tersimpan)

  /** Catat setoran + mutasi kasnya. Tanpa mutasi, saldo akun tak bergerak dan analisa
   *  selisih tak pernah bisa menduga "cocok dengan setoran hari ini". */
  async function tambahSetoran(row: repo.SetoranRow): Promise<void> {
    try {
      if (row.dariId) {
        row.moveId = row.keId
          ? await akunRepo.pindahAkun({ tgl: kTgl, dariId: row.dariId, keId: row.keId, jumlah: row.jumlah, jenis: 'setoran', catatan: row.tujuan || null, outlet: kCab || null })
          : await akunRepo.tambahMutasi({ tgl: kTgl, akunId: row.dariId, arah: 'keluar', jumlah: row.jumlah, jenis: 'setoran', catatan: row.tujuan || null, outlet: kCab || null });
      }
    } catch (e) { toast('Setoran dicatat, tapi mutasi kas gagal: ' + (e as Error).message); }
    await simpan([...setoran, row], bacaAktual(), false);
  }
  root.querySelector('#ks_addset')?.addEventListener('click', () => setoranDialog((row) => void tambahSetoran(row), 0, akunKas));
  root.querySelectorAll<HTMLElement>('button[data-delset]').forEach((b) => b.addEventListener('click', () => void (async () => {
    const i = +(b.dataset.delset ?? -1);
    const hapus = setoran[i];
    // Mutasinya ikut dibatalkan — baris hilang tapi saldo akun tetap berkurang = selisih tanpa jejak.
    if (hapus?.moveId) { try { await akunRepo.hapusMutasi(hapus.moveId); } catch (e) { toast((e as Error).message); } }
    await simpan(setoran.filter((_, k) => k !== i), bacaAktual(), false);
  })()));

  async function simpan(set: repo.SetoranRow[], act: Record<string, number>, tutup: boolean): Promise<void> {
    const catatan = (root.querySelector('#ks_catatan') as HTMLTextAreaElement | null)?.value ?? '';
    if (tutup) {
      const d = selisih(expect, act);
      const adaSelisih = Object.values(d).some((v) => v !== 0) && Object.keys(act).length > 0;
      // Selisih TIDAK diblokir — memblokir hanya membuat angka dikarang agar pas. Tapi wajib beralasan.
      if (adaSelisih && !catatan.trim()) { toast('Ada selisih — isi catatan penutupan dulu.'); return; }
    }
    try {
      // Saat menutup, bekukan angka LIVE; saat sekadar menyimpan, jangan sentuh snapshot yang sudah ada.
      await svc.saveClose({ tgl: kTgl, outlet: kCab || null, expect: tutup ? expectLive : expect, actual: act, setoran: set, catatan: catatan.trim() || null, closed_by: null, closed_at: null }, tutup);
      toast(tutup ? '🔒 Hari ditutup' : '💾 Tersimpan');
      refresh();
    } catch (e) { toast((e as Error).message); }
  }

  root.querySelector('#ks_riwayat')?.addEventListener('click', () => { kMode = 'riwayat'; void renderTutupKasir(root); });
  // Satu klik: jumlahnya sudah terisi sebesar yang perlu keluar agar laci kembali ke kas kecil.
  root.querySelector('#ks_setorcepat')?.addEventListener('click', () =>
    setoranDialog((row) => void tambahSetoran(row), setorSaran, akunKas));
  root.querySelector('#ks_tutup')?.addEventListener('click', () => void simpan(setoran, bacaAktual(), true));
  root.querySelector('#ks_verif')?.addEventListener('click', async () => {
    try { await svc.verifyClose(kTgl, kCab || null, close?.closed_by ?? null); toast('✓ Penutupan diverifikasi'); refresh(); }
    catch (e) { toast((e as Error).message); }
  });

  root.querySelector('#ks_xls')?.addEventListener('click', () => {
    const th = ['No. Servis', 'Pelanggan', 'Unit', 'Sumber', 'Metode', 'Jumlah'];
    const body = [...kas.masuk, ...kas.keluar.map((r) => ({ ...r, jumlah: -r.jumlah }))]
      .map((r) => `<tr><td>${esc(r.no)}</td><td>${esc(r.custNama)}</td><td>${esc(r.unit)}</td><td>${esc(sumberLabel[r.sumber] ?? r.sumber)}</td><td>${esc(r.metode)}</td><td class="r">${r.jumlah}</td></tr>`).join('');
    const ringkas = metodeAda.map((m) => `<tr><td>${esc(m)}</td><td class="r">${expect[m] ?? 0}</td><td class="r">${actual[m] ?? ''}</td><td class="r">${actual[m] == null ? '' : dif[m]}</td></tr>`).join('');
    void downloadXLS(`tutup-kasir-${kTgl}${kCab ? '-' + kCab : ''}.xls`,
      `<h2>Tutup Kasir — iFiCs</h2><p>Tanggal ${esc(kTgl)} · ${esc(kCab || 'Semua cabang')} · Unit keluar ${keluar.length} · Uang masuk ${rp(kas.totalMasuk)} · Refund ${rp(kas.totalKeluar)} · Piutang baru ${rp(kas.tempoBaru)}</p>
       <table><tr><th>Metode</th><th>Seharusnya</th><th>Fisik</th><th>Selisih</th></tr>${ringkas}</table><br>
       <table><tr>${th.map((h) => `<th>${h}</th>`).join('')}</tr>${body}</table>`);
  });
}

/**
 * "Di mana letak selisihnya?" — menyodorkan TERSANGKA berikut notanya, bukan sekadar mengumumkan angka.
 * Yang nilainya sama persis dengan selisih ditandai & ditaruh paling atas: dalam praktik kasir,
 * kecocokan angka persis hampir selalu berarti itulah penyebabnya.
 */
function rekonDialog(totalDif: number, petunjuk: Petunjuk[], perMetode: Record<string, number>, perAkun: { akun: string; dugaan: DugaanSelisih[] }[] = []): void {
  const arahTxt = totalDif > 0 ? 'LEBIH banyak dari catatan' : 'KURANG dari catatan';
  const perM = Object.entries(perMetode).filter(([, v]) => v !== 0)
    .map(([m, v]) => `<tr><td>${esc(m)}</td><td class="r" style="color:${v > 0 ? 'var(--ok)' : 'var(--danger)'};font-weight:700">${v > 0 ? '+' : ''}${rp(v)}</td></tr>`).join('')
    || '<tr><td colspan="2" class="empty">—</td></tr>';

  const kartu = petunjuk.map((p) => `
    <div class="panel" style="margin:0 0 8px;border-color:${p.cocokPersis ? 'var(--ok)' : 'var(--line)'};background:${p.cocokPersis ? 'var(--okbg)' : '#fff'}">
      <div style="display:flex;gap:8px;align-items:baseline;flex-wrap:wrap">
        <b>${p.cocokPersis ? '🎯 ' : ''}${esc(p.judul)}</b>
        <b style="margin-left:auto;color:${p.cocokPersis ? 'var(--ok)' : 'var(--navy)'}">${rp(p.jumlah)}</b>
      </div>
      ${p.cocokPersis ? '<div style="font-size:12px;font-weight:700;color:var(--ok);margin-top:2px">Nilainya SAMA PERSIS dengan selisih — kemungkinan besar inilah penyebabnya.</div>' : ''}
      <div class="scope-note" style="margin:6px 0 0">${esc(p.penjelasan)}</div>
      ${p.nota.length ? `<div style="font-size:12px;margin-top:6px">Terkait: ${p.nota.map((x) => `<b>${esc(x)}</b>`).join(' · ')}</div>` : ''}
    </div>`).join('')
    || `<div class="panel"><div class="empty">Tidak ada penyebab yang bisa ditelusuri dari data.<br><span style="font-size:12px">Kemungkinan besar soal fisik: kembalian, uang belum disetor/dicatat, atau salah hitung. Catat selisihnya apa adanya lalu periksa laci.</span></div></div>`;

  const bg = openModal(`<div class="modal-head"><h3>🔍 Letak selisih</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note" style="background:${totalDif > 0 ? 'var(--warnbg)' : 'var(--dangerbg)'};color:${totalDif > 0 ? 'var(--warn)' : 'var(--danger)'}">
        Uang fisik <b>${arahTxt}</b> sebesar <b>${rp(Math.abs(totalDif))}</b>.</div>
      <div class="ttl" style="font-size:13px;margin:12px 0 4px">Selisih per metode</div>
      <table class="data nosort"><thead><tr><th>Metode</th><th class="r">Selisih</th></tr></thead><tbody>${perM}</tbody></table>
      ${perAkun.length ? `<div class="ttl" style="font-size:13px;margin:14px 0 6px">Pembacaan per akun</div>
        ${perAkun.map((x) => `<div class="panel" style="margin:0 0 8px">
          <b>${esc(x.akun)}</b>
          ${x.dugaan.map((d) => {
            const w = d.tingkat === 'bahaya' ? 'var(--danger)' : d.tingkat === 'perhatian' ? 'var(--warn)' : 'var(--ok)';
            return `<div style="margin-top:6px;padding-left:9px;border-left:3px solid ${w}">
              <div style="font-weight:700;color:${w};font-size:12.5px">${esc(d.judul)}</div>
              <div style="font-size:12px;color:var(--muted);line-height:1.45">${esc(d.pesan)}</div></div>`;
          }).join('')}
        </div>`).join('')}` : ''}
      <div class="ttl" style="font-size:13px;margin:14px 0 6px">Kemungkinan penyebab</div>
      ${kartu}
    </div>
    <div class="modal-foot"><button class="btn" id="rk_close">Tutup</button></div>`);
  bg.querySelector('#rk_close')?.addEventListener('click', () => bg.remove());
}

/** Dialog catat setoran — bank dan tunai dicatat terpisah (permintaan Apo). */
function setoranDialog(onOk: (row: repo.SetoranRow) => void, saran = 0, akun: akunRepo.AkunRow[] = []): void {
  // Akun asal = laci tunai (uangnya memang keluar dari sana); tujuan = rekening/e-wallet.
  const asal = akun.filter((a) => a.jenis === 'tunai');
  const tujuanAkun = akun.filter((a) => a.jenis !== 'tunai');
  const opt = (arr: akunRepo.AkunRow[], pilih?: number): string =>
    arr.map((a) => `<option value="${a.id}" ${pilih === a.id ? 'selected' : ''}>${esc(a.nama)}</option>`).join('');
  const bg = openModal(`<div class="modal-head"><h3>➕ Catat Setoran</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="field"><label>Jenis</label><select id="st_jenis"><option value="bank">🏦 Setor ke bank</option><option value="tunai">💵 Serah tunai</option></select></div>
      ${asal.length ? `<div class="field"><label>Keluar dari akun</label><select id="st_dari">${opt(asal, asal[0]?.id)}</select></div>` : ''}
      ${tujuanAkun.length ? `<div class="field" id="st_kebox"><label>Masuk ke akun</label><select id="st_ke"><option value="">— tidak masuk akun mana pun —</option>${opt(tujuanAkun)}</select>
        <div class="hint">Pilih rekening tujuan supaya saldonya ikut bertambah. Kosongkan untuk uang yang diserahkan ke orang (keluar dari laci, tidak masuk akun).</div></div>` : ''}
      <div class="field"><label>Tujuan</label><input id="st_tujuan" placeholder="mis. BCA 1234567890 / diserahkan ke Apo"></div>
      <div class="field"><label>Jumlah <span class="req">*</span></label><input id="st_jumlah" inputmode="numeric" placeholder="Rp 0" value="${saran > 0 ? rp(saran) : ''}">
        ${saran > 0 ? `<div class="hint">Terisi sebesar uang yang perlu keluar agar laci kembali ke kas kecil. Ubah bila yang disetor berbeda.</div>` : ''}</div>
      <div class="field"><label>Ref / No. bukti</label><input id="st_ref" placeholder="opsional"></div>
      <div class="err" id="st_err"></div>
    </div>
    <div class="modal-foot"><button class="btn" id="st_no">Batal</button><button class="btn primary" id="st_yes">Simpan</button></div>`);
  const jml = bg.querySelector('#st_jumlah') as HTMLInputElement;
  jml.addEventListener('input', () => { jml.value = jml.value.trim() ? rpFmt(jml.value) : ''; });
  const jenisSel = bg.querySelector('#st_jenis') as HTMLSelectElement;
  const keBox = bg.querySelector('#st_kebox') as HTMLElement | null;
  const syncKe = (): void => { if (keBox) keBox.style.display = jenisSel.value === 'tunai' ? 'none' : ''; };
  jenisSel.addEventListener('change', () => { if (jenisSel.value === 'tunai') { const k = bg.querySelector('#st_ke') as HTMLSelectElement | null; if (k) k.value = ''; } syncKe(); });
  syncKe();
  bg.querySelector('#st_no')?.addEventListener('click', () => bg.remove());
  bg.querySelector('#st_yes')?.addEventListener('click', () => {
    const jumlah = rpVal(jml.value);
    if (jumlah <= 0) { const e = bg.querySelector('#st_err'); if (e) e.textContent = 'Jumlah harus lebih dari 0.'; return; }
    const numOf = (id: string): number | undefined => { const v = +((bg.querySelector('#' + id) as HTMLSelectElement | null)?.value ?? ''); return v > 0 ? v : undefined; };
    onOk({
      jenis: ((bg.querySelector('#st_jenis') as HTMLSelectElement).value === 'tunai' ? 'tunai' : 'bank'),
      tujuan: (bg.querySelector('#st_tujuan') as HTMLInputElement).value.trim(),
      jumlah,
      ref: (bg.querySelector('#st_ref') as HTMLInputElement).value.trim(),
      dariId: numOf('st_dari'), keId: numOf('st_ke'),
    });
    bg.remove();
  });
}


// ── Riwayat penutupan & laporan bulanan ───────────────────────────────────────

const BULAN = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];

/** Hari terakhir sebuah bulan (mm 1–12). Dipakai batas rentang, jangan ditebak 30/31. */
const akhirBulan = (th: number, bl: number): string =>
  `${th}-${String(bl).padStart(2, '0')}-${String(new Date(th, bl, 0).getDate()).padStart(2, '0')}`;

async function renderRiwayat(root: HTMLElement): Promise<void> {
  const kini = new Date();
  if (!rBulan) rBulan = String(kini.getMonth() + 1);
  if (!rTahun) rTahun = String(kini.getFullYear());
  root.innerHTML = `<div class="panel"><div class="empty">Memuat riwayat…</div></div>`;

  const th = +rTahun, bl = +rBulan;
  const dari = `${th}-${String(bl).padStart(2, '0')}-01`;
  const sampai = akhirBulan(th, bl);

  let rows: repo.DailyClose[]; let cabangs: { kode: string; nama: string }[] = [];
  try {
    rows = await repo.listCloses({ dari, sampai, outlet: kCab || null });
    if (isMgr()) cabangs = await listCabang();
  } catch (e) { root.innerHTML = `<div class="panel err">${esc((e as Error).message)}</div>`; return; }

  const baris: BarisPenutupan[] = rows
    .map((r) => baris1Penutupan({ tgl: r.tgl, outlet: r.outlet, expect: r.expect ?? {}, actual: r.actual ?? {},
      setoran: (r.setoran ?? []) as { jenis: string; jumlah: number }[], closed_at: r.closed_at, verified_at: r.verified_at }))
    .sort((a, b) => a.tgl.localeCompare(b.tgl));
  const total = ringkasPenutupan(baris);

  const lencana = (s: BarisPenutupan['status']): string =>
    s === 'terverifikasi' ? '<span class="pill" style="background:var(--okbg);color:var(--ok)">✓ terverifikasi</span>'
    : s === 'ditutup' ? '<span class="pill" style="background:var(--warnbg);color:var(--warn)">🔒 ditutup</span>'
    : '<span class="pill" style="background:#eee;color:#666">draft</span>';

  const isi = baris.map((b) => `<tr${b.selisih !== 0 && b.dihitung ? ' style="background:var(--dangerbg)"' : ''}>
      <td><b>${esc(b.tgl)}</b></td>
      <td>${esc(b.outlet ?? 'Semua')}</td>
      <td class="r">${b.dihitung ? rp(b.seharusnya) : '—'}</td>
      <td class="r">${b.dihitung ? rp(b.fisik) : '<span style="color:var(--muted)">belum dihitung</span>'}</td>
      <td class="r" style="font-weight:700;color:${!b.dihitung ? 'var(--muted)' : b.selisih === 0 ? 'var(--ok)' : 'var(--danger)'}">${
        b.dihitung ? (b.selisih > 0 ? '+' : '') + rp(b.selisih) : '—'}</td>
      <td class="r">${rp(b.setoran)}</td>
      <td>${lencana(b.status)}</td>
      <td><button class="btn" style="padding:3px 8px" data-buka="${esc(b.tgl)}" data-cab="${esc(b.outlet ?? '')}">Buka</button></td>
    </tr>`).join('') || '<tr><td colspan="8" class="empty">Belum ada penutupan pada bulan ini.</td></tr>';

  const opsiBulan = BULAN.map((n, i) => `<option value="${i + 1}" ${bl === i + 1 ? 'selected' : ''}>${n}</option>`).join('');
  const opsiTahun = Array.from({ length: 5 }, (_, i) => kini.getFullYear() - i)
    .map((y) => `<option ${th === y ? 'selected' : ''}>${y}</option>`).join('');

  root.innerHTML = `
    <div class="scope-note">Riwayat penutupan kasir per bulan. <b>Hari yang belum dihitung fisik tidak dianggap selisih</b> —
      ia ditandai "belum dihitung" supaya laporan tak menuduh kehilangan uang yang sebenarnya hanya belum dihitung.</div>
    <div class="toolbar" style="display:flex;gap:8px;align-items:center;margin:10px 0;flex-wrap:wrap">
      <button class="btn" id="rw_kembali" style="padding:5px 10px">← Penutupan harian</button>
      <select id="rw_bln" aria-label="Bulan">${opsiBulan}</select>
      <select id="rw_thn" aria-label="Tahun">${opsiTahun}</select>
      ${isMgr() ? `<select id="rw_cab" aria-label="Cabang"><option value="">Semua cabang</option>${
        cabangs.map((c) => `<option value="${esc(c.kode)}" ${kCab === c.kode ? 'selected' : ''}>${esc(c.nama)}</option>`).join('')}</select>` : ''}
      <button class="btn" id="rw_xls" style="margin-left:auto;padding:5px 10px">⬇️ Excel bulan ini</button>
    </div>

    <div class="stat-grid" style="margin:8px 0 12px">
      ${stat('Hari tercatat', `${total.hari}`)}
      ${stat('Sudah dihitung', `${total.dihitung}`)}
      ${stat('Terverifikasi', `${total.terverifikasi}`, 'var(--ok)')}
      ${stat('Total seharusnya', rp(total.seharusnya))}
      ${stat('Total dihitung fisik', rp(total.fisik))}
      ${stat('Selisih sebulan', (total.selisih > 0 ? '+' : '') + rp(total.selisih), total.selisih === 0 ? 'var(--ok)' : 'var(--danger)')}
      ${stat('Total setoran', rp(total.setoran))}
      ${stat('Hari ada selisih', `${total.hariSelisih}`, total.hariSelisih ? 'var(--warn)' : 'var(--ok)')}
    </div>

    <div style="overflow-x:auto"><table class="data">
      <thead><tr><th>Tanggal</th><th>Cabang</th><th class="r">Seharusnya</th><th class="r">Dihitung fisik</th>
        <th class="r">Selisih</th><th class="r">Setoran</th><th>Status</th><th></th></tr></thead>
      <tbody>${isi}</tbody>
      <tfoot><tr><th colspan="2" class="r">TOTAL ${esc(BULAN[bl - 1] ?? '')} ${esc(rTahun)}</th>
        <th class="r">${rp(total.seharusnya)}</th><th class="r">${rp(total.fisik)}</th>
        <th class="r">${(total.selisih > 0 ? '+' : '') + rp(total.selisih)}</th>
        <th class="r">${rp(total.setoran)}</th><th colspan="2"></th></tr></tfoot>
    </table></div>`;

  const ulang = (): void => void renderRiwayat(root);
  root.querySelector('#rw_kembali')?.addEventListener('click', () => { kMode = 'hari'; void renderTutupKasir(root); });
  root.querySelector('#rw_bln')?.addEventListener('change', (e) => { rBulan = (e.target as HTMLSelectElement).value; ulang(); });
  root.querySelector('#rw_thn')?.addEventListener('change', (e) => { rTahun = (e.target as HTMLSelectElement).value; ulang(); });
  root.querySelector('#rw_cab')?.addEventListener('change', (e) => { kCab = (e.target as HTMLSelectElement).value; ulang(); });
  root.querySelectorAll<HTMLElement>('button[data-buka]').forEach((b) => b.addEventListener('click', () => {
    kTgl = b.dataset['buka'] ?? kTgl; kCab = b.dataset['cab'] ?? kCab; kMode = 'hari'; void renderTutupKasir(root);
  }));

  root.querySelector('#rw_xls')?.addEventListener('click', () => {
    // Baris TOTAL ikut di dalam berkas: yang membuka Excel-nya belum tentu membuka aplikasinya.
    const tr = baris.map((b) => `<tr><td>${esc(b.tgl)}</td><td>${esc(b.outlet ?? 'Semua')}</td>
      <td class="r">${b.dihitung ? b.seharusnya : ''}</td><td class="r">${b.dihitung ? b.fisik : ''}</td>
      <td class="r">${b.dihitung ? b.selisih : ''}</td><td class="r">${b.setoran}</td>
      <td>${b.dihitung ? '' : 'belum dihitung'}</td><td>${esc(b.status)}</td></tr>`).join('');
    void downloadXLS(`tutup-kasir-${rTahun}-${String(bl).padStart(2, '0')}${kCab ? '-' + kCab : ''}.xls`,
      `<h2>Riwayat Tutup Kasir — iFiCs</h2>
       <p>Periode ${esc(BULAN[bl - 1] ?? '')} ${esc(rTahun)} · ${esc(kCab || 'Semua cabang')} ·
          Hari tercatat ${total.hari} · Sudah dihitung ${total.dihitung} · Terverifikasi ${total.terverifikasi} ·
          Hari ada selisih ${total.hariSelisih}</p>
       <table>
         <tr><th>Tanggal</th><th>Cabang</th><th>Seharusnya</th><th>Dihitung fisik</th><th>Selisih</th><th>Setoran</th><th>Catatan</th><th>Status</th></tr>
         ${tr}
         <tr><th colspan="2">TOTAL</th><th>${total.seharusnya}</th><th>${total.fisik}</th><th>${total.selisih}</th><th>${total.setoran}</th><th colspan="2"></th></tr>
       </table>`);
  });
}
